import type { IconDefinition } from '@fortawesome/fontawesome-common-types'
import { faStar, faThumbsDown, faThumbsUp } from '@fortawesome/pro-regular-svg-icons'

import {
  AUDIT_RUBRIC_RESPONSES_LEVEL,
  AUDIT_RUBRIC_RESPONSES_YES_NO,
  AuditRating,
  type AuditReplyRating,
  type AuditRubricResponse,
  type AuditRubricVersion,
  ItemResponse,
  type MarsKey,
  TutorAuditorLevel,
} from './types'

export const AUDIT_RUBRIC_VERSIONS = [
  'lead-v1',
  'mars-v2',
  'mars-sat-v3',
  'mars-sat-v2',
  'mars-self-sat-v2',
] as const

export const LATEST_AUDIT_RUBRIC_VERSION: AuditRubricVersion = 'lead-v1'

export const NEW_AUDITOR_SESSIONS_PENDING_REVIEW_THRESHOLD = 3
export const AUDITOR_SESSIONS_HOSTED_MIN_THRESHOLD = 5
export const AUDIT_VOLUNTEER_MINUTES = 30

export const SAFETY_REPORT_SLUG = 'safety-1'
export const COACH_ESCALATION_SLUG = 'internal-1'
export const INTERNAL_COMMENT_SLUG = 'internal-comment-1'

export const MAX_RESPONSES_PER_AUDIT_RUBRIC_QUESTION = Math.max(
  AUDIT_RUBRIC_RESPONSES_LEVEL.length,
  AUDIT_RUBRIC_RESPONSES_YES_NO.length
)

export const AUDITOR_LEVELS = [
  TutorAuditorLevel.Flagged,
  TutorAuditorLevel.Novice,
  TutorAuditorLevel.Beginner,
  TutorAuditorLevel.Intermediate,
  TutorAuditorLevel.Advanced,
]

export const EXPERIENCED_AUDIT_LEVELS = [
  TutorAuditorLevel.Beginner,
  TutorAuditorLevel.Intermediate,
  TutorAuditorLevel.Advanced,
]

export const TUTOR_AUDITOR_LEVEL_TO_NUMERIC_LEVEL: Record<TutorAuditorLevel, number> = {
  [TutorAuditorLevel.Flagged]: -1,
  [TutorAuditorLevel.Novice]: 0,
  [TutorAuditorLevel.Beginner]: 1,
  [TutorAuditorLevel.Intermediate]: 2,
  [TutorAuditorLevel.Advanced]: 3,
}

export const TUTOR_AUDITOR_LEVEL_TO_LABEL: Record<TutorAuditorLevel, string> = {
  [TutorAuditorLevel.Flagged]: 'Flagged',
  [TutorAuditorLevel.Novice]: 'Novice',
  [TutorAuditorLevel.Beginner]: 'Beginner',
  [TutorAuditorLevel.Intermediate]: 'Intermediate',
  [TutorAuditorLevel.Advanced]: 'Advanced',
}

export const TUTOR_AUDITOR_LEVEL_TO_EXPLANATION: Record<TutorAuditorLevel, string | null> = {
  // Audits from flagged auditors shouldn't show up in practice.
  [TutorAuditorLevel.Flagged]: null,
  [TutorAuditorLevel.Novice]:
    'This peer reviewer is new, and is still in training! Your session will also be reviewed by a more experienced peer reviewer.',
  [TutorAuditorLevel.Beginner]:
    'This peer reviewer is still working to build their experience as an peer reviewer. Your session will also be reviewed by a more experienced peer reviewer.',
  [TutorAuditorLevel.Intermediate]:
    'This peer reviewer has given tutors lots of great feedback in the past. Their feedback is likely very helpful!',
  [TutorAuditorLevel.Advanced]:
    'This peer reviewer is one of our most experienced on the platform. Be sure to listen closely!',
}

export const AUDIT_RUBRIC_RESPONSE_TO_LABEL: Record<AuditRubricResponse, string> = {
  'level-1': 'Needs Improvement',
  'level-2': 'Beginner',
  'level-3': 'Intermediate',
  'level-4': 'Advanced',
  'level-0': 'N/A',
  yes: 'Yes',
  no: 'No',
}

export const AUDIT_REPLY_RATING_TO_UI_DETAILS: Record<AuditReplyRating, { icon: IconDefinition }> =
  {
    Yes: { icon: faStar },
    Somewhat: { icon: faThumbsUp },
    No: { icon: faThumbsDown },
  }

// This is now deprecated, but keeping around for old sessions.

export const MARS_SECTIONS: { label: string; keys: MarsKey[] }[] = [
  {
    label: 'Mastery',
    keys: ['mastery-1'],
  },
  {
    label: 'Active Learning',
    keys: ['active-1', 'active-2', 'active-3'],
  },
  {
    label: 'Respectful Community',
    keys: ['respect-1'],
  },
  {
    label: 'Safety',
    keys: [SAFETY_REPORT_SLUG],
  },
]

export const MARS_ITEM_RESPONSE_TO_LABEL: Record<ItemResponse, string> = {
  [ItemResponse.Yes]: 'Yes',
  [ItemResponse.No]: 'No',
  [ItemResponse.NotApplicable]: 'N/A',
}

export const MARS_KEY_TO_LABEL: Record<MarsKey, string> = {
  'mastery-1': 'Did the tutor go through the content without significant errors?',
  'active-1':
    'Did the tutor successfully invite questions from learners and answer them appropriately?',
  'active-2': 'Did learners work on practice problems or activities during the session?',
  'active-3':
    'Did the tutor ask learners questions to figure out what they did and did not understand?',
  'respect-1': 'Was dialogue between learners and tutors respectful at all times?',
  [SAFETY_REPORT_SLUG]: 'Did the tutor create a safe environment for learners?',
  'mentor-1': 'Were there any other issues with the session that you want to escalate to a coach?',
}

export const AUDIT_RATING_TYPE_TO_LABEL: Record<AuditRating, string> = {
  [AuditRating.None]: 'Did Not Attend',
  [AuditRating.Flag]: 'Flag',
  [AuditRating.Novice]: 'Novice',
  [AuditRating.Proficient]: 'Proficient',
  [AuditRating.Advanced]: 'Advanced',
}

export const PEER_REVIEW_RECENT_SESSION_THRESHOLD_DAYS = 10
